/** Shared delivery-address selection (same-tab sync + pin icons by type). */

export type AddressLocationType = 0 | 1 | 2 | 3 | 4;

/** 0=apartment, 1=house/home, 2=office/work, 3=outdoor, 4=elsewhere */
export const ADDRESS_TYPE_META: Record<
  number,
  { icon: string; iconFill: string; labelKey: string }
> = {
  0: { icon: 'ri-building-line', iconFill: 'ri-building-fill', labelKey: 'apartment' },
  1: { icon: 'ri-home-smile-line', iconFill: 'ri-home-smile-fill', labelKey: 'house' },
  2: { icon: 'ri-briefcase-line', iconFill: 'ri-briefcase-fill', labelKey: 'office' },
  3: { icon: 'ri-tree-line', iconFill: 'ri-tree-fill', labelKey: 'outdoor' },
  4: { icon: 'ri-map-pin-line', iconFill: 'ri-map-pin-fill', labelKey: 'elsewhere' },
};

export function getAddressTypeIcon(type: number | null | undefined, filled = false): string {
  const meta = ADDRESS_TYPE_META[type ?? 4] ?? ADDRESS_TYPE_META[4];
  return filled ? meta.iconFill : meta.icon;
}

export interface DeliveryAddressPayload {
  display: string;
  id?: string | number | null;
  lat?: number | null;
  lon?: number | null;
  type?: number | null;
}

const EVENT = 'deliveryAddressChanged';

export function persistDeliveryAddress(payload: DeliveryAddressPayload) {
  if (payload.display) {
    localStorage.setItem('selectedDeliveryAddress', payload.display);
  }
  if (payload.id != null && payload.id !== '') {
    localStorage.setItem('selectedDeliveryAddressId', String(payload.id));
  }
  if (payload.lat != null && payload.lon != null && !Number.isNaN(payload.lat) && !Number.isNaN(payload.lon)) {
    localStorage.setItem('selectedDeliveryLat', String(payload.lat));
    localStorage.setItem('selectedDeliveryLon', String(payload.lon));
  }
  if (payload.type != null) {
    localStorage.setItem('selectedDeliveryAddressType', String(payload.type));
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: payload }));
  window.dispatchEvent(new Event('addressCoordsChanged'));
}

export function readDeliveryAddress(): DeliveryAddressPayload {
  const lat = localStorage.getItem('selectedDeliveryLat');
  const lon = localStorage.getItem('selectedDeliveryLon');
  const type = localStorage.getItem('selectedDeliveryAddressType');
  return {
    display: localStorage.getItem('selectedDeliveryAddress') || '',
    id: localStorage.getItem('selectedDeliveryAddressId'),
    lat: lat != null ? parseFloat(lat) : null,
    lon: lon != null ? parseFloat(lon) : null,
    type: type != null ? Number(type) : null,
  };
}

export function clearDeliveryAddress() {
  localStorage.removeItem('selectedDeliveryAddress');
  localStorage.removeItem('selectedDeliveryAddressId');
  localStorage.removeItem('selectedDeliveryLat');
  localStorage.removeItem('selectedDeliveryLon');
  localStorage.removeItem('selectedDeliveryAddressType');
  const empty: DeliveryAddressPayload = {
    display: '',
    id: null,
    lat: null,
    lon: null,
    type: null,
  };
  window.dispatchEvent(new CustomEvent(EVENT, { detail: empty }));
  window.dispatchEvent(new Event('addressCoordsChanged'));
}

export function subscribeDeliveryAddress(handler: (p: DeliveryAddressPayload) => void): () => void {
  const onCustom = (e: Event) => {
    const detail = (e as CustomEvent<DeliveryAddressPayload>).detail;
    handler(detail ?? readDeliveryAddress());
  };
  const onStorage = () => handler(readDeliveryAddress());
  window.addEventListener(EVENT, onCustom);
  window.addEventListener('storage', onStorage);
  window.addEventListener('addressCoordsChanged', onStorage);
  return () => {
    window.removeEventListener(EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('addressCoordsChanged', onStorage);
  };
}

export { EVENT as DELIVERY_ADDRESS_EVENT };
